// Creative Strategist home (Task #2375, spec §8).
//
// "What do you want to build?" hero → creates a draft project (status
// `planning`) and lands on its detail page. Below the hero: Active Projects
// cards and a Recently Completed list.
//
// Chat, file uploads and context assets are a later phase — the hero here is a
// plain text objective input.

import { useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useLocation, useParams } from "wouter";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Skeleton } from "@/components/ui/skeleton";
import { Progress } from "@/components/ui/progress";
import {
  AlertTriangle,
  Compass,
  ArrowRight,
  CheckCircle2,
  Layers,
  Loader2,
  Sparkles,
} from "lucide-react";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import {
  CS_PROJECT_STATUS_CLASSES,
  CS_PROJECT_STATUS_LABELS,
  CS_STARTER_PROMPTS,
  CS_TERMINAL_STATUSES,
  csFormatDateTime,
  csStatusKey,
  type CreativeStrategistProject,
  type CreativeStrategistStatus,
} from "@/lib/creative-strategist";

export default function CreativeStrategistHomePage() {
  const { brandId } = useParams<{ brandId: string }>();
  const [, navigate] = useLocation();
  const { toast } = useToast();
  const [objective, setObjective] = useState("");

  const projectsKey = `/api/creative-strategist/brands/${brandId}/projects`;

  const { data: projects, isLoading } = useQuery<CreativeStrategistProject[]>({
    queryKey: [projectsKey],
    enabled: !!brandId,
  });

  // The guard already refuses to render this page unless the status route says
  // the feature is open; here it only distinguishes "open" from "paused".
  const { data: status } = useQuery<CreativeStrategistStatus>({
    queryKey: [csStatusKey(brandId)],
    enabled: !!brandId,
  });

  const newWorkPaused = status?.mode === "disable_new_work";

  const createProject = useMutation({
    mutationFn: async (text: string) => {
      const res = await apiRequest("POST", projectsKey, { objective: text });
      return (await res.json()) as CreativeStrategistProject;
    },
    onSuccess: (project) => {
      queryClient.invalidateQueries({ queryKey: [projectsKey] });
      setObjective("");
      // Land in the conversation: the hero starts the project, the chat panel
      // is where the user keeps talking to the strategist (Task #2377).
      navigate(`/brands/${brandId}/creative-strategist/projects/${project.id}?chat=1`);
    },
    onError: (e: Error) => {
      toast({ title: "Couldn't start that project", description: e.message, variant: "destructive" });
    },
  });

  const submit = () => {
    const text = objective.trim();
    if (!text || createProject.isPending) return;
    createProject.mutate(text);
  };

  const active = (projects ?? []).filter((p) => !CS_TERMINAL_STATUSES.has(p.status));
  const completed = (projects ?? [])
    .filter((p) => p.status === "complete")
    .sort((a, b) => (b.completedAt ?? "").localeCompare(a.completedAt ?? ""))
    .slice(0, 10);

  return (
    <div className="p-8 max-w-5xl mx-auto space-y-10">
      {/* Hero */}
      <div className="space-y-5 fade-in-up">
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Compass className="h-4 w-4" />
            Creative Strategist
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate(`/brands/${brandId}/creative-strategist/templates`)}
            data-testid="button-creative-system"
          >
            <Layers className="h-3.5 w-3.5 mr-1.5" /> Creative System
          </Button>
        </div>
        <h1
          className="text-3xl font-bold tracking-tight"
          data-testid="text-creative-strategist-title"
        >
          What do you want to build?
        </h1>
        <p className="text-sm text-muted-foreground -mt-3">
          Describe the outcome. Creative Strategist plans the work and runs it through the tools you
          already use.
        </p>

        <div className="space-y-3">
          <Textarea
            value={objective}
            onChange={(e) => setObjective(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) submit();
            }}
            rows={4}
            placeholder="e.g. Build a paid acquisition campaign for the summer sale"
            className="text-base resize-none"
            data-testid="input-creative-strategist-objective"
          />
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div className="text-xs text-muted-foreground">
              {newWorkPaused
                ? "New projects are paused right now — in-flight work continues."
                : "⌘/Ctrl + Enter to start"}
            </div>
            <Button
              onClick={submit}
              disabled={!objective.trim() || createProject.isPending || newWorkPaused}
              className="rounded-full px-5"
              data-testid="button-creative-strategist-start"
            >
              {createProject.isPending ? (
                <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />
              ) : (
                <Sparkles className="h-4 w-4 mr-1.5" />
              )}
              Start project
            </Button>
          </div>
        </div>

        <div className="flex flex-wrap gap-2">
          {CS_STARTER_PROMPTS.map((prompt) => (
            <button
              key={prompt}
              type="button"
              onClick={() => setObjective(prompt)}
              className="rounded-full border px-3 py-1.5 text-xs text-muted-foreground hover:bg-muted hover:text-foreground transition-colors"
              data-testid={`button-starter-prompt-${prompt.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
            >
              {prompt}
            </button>
          ))}
        </div>
      </div>

      {/* Active projects */}
      <section className="space-y-4 fade-in-up-delay-1">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold tracking-tight">Active Projects</h2>
          {(projects?.length ?? 0) > 0 && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate(`/brands/${brandId}/creative-strategist/projects`)}
              data-testid="button-view-all-projects"
            >
              View all <ArrowRight className="h-3.5 w-3.5 ml-1" />
            </Button>
          )}
        </div>

        {isLoading ? (
          <div className="grid gap-4 md:grid-cols-2">
            <Skeleton className="h-36 w-full" />
            <Skeleton className="h-36 w-full" />
          </div>
        ) : active.length === 0 ? (
          <Card>
            <CardContent className="py-10 text-center text-sm text-muted-foreground">
              No active projects yet. Describe what you want to build above.
            </CardContent>
          </Card>
        ) : (
          <div className="grid gap-4 md:grid-cols-2">
            {active.map((project) => (
              <ProjectCard key={project.id} project={project} brandId={brandId!} />
            ))}
          </div>
        )}
      </section>

      {/* Recently completed */}
      <section className="space-y-4 fade-in-up-delay-2">
        <h2 className="text-lg font-semibold tracking-tight">Recently Completed</h2>
        {completed.length === 0 ? (
          <Card>
            <CardContent className="py-8 text-center text-sm text-muted-foreground">
              Nothing completed yet.
            </CardContent>
          </Card>
        ) : (
          <Card>
            <CardContent className="p-0 divide-y">
              {completed.map((project) => (
                <button
                  key={project.id}
                  type="button"
                  onClick={() =>
                    navigate(`/brands/${brandId}/creative-strategist/projects/${project.id}`)
                  }
                  className="w-full flex items-center justify-between gap-4 px-4 py-3 text-left hover:bg-muted/50 transition-colors"
                  data-testid={`row-completed-project-${project.id}`}
                >
                  <div className="min-w-0">
                    <div className="text-sm font-medium truncate">{project.title}</div>
                    <div className="text-xs text-muted-foreground">
                      {project.stats.deliverablesTotal} deliverables ·{" "}
                      {csFormatDateTime(project.completedAt)}
                    </div>
                  </div>
                  <CheckCircle2 className="h-4 w-4 text-emerald-600 shrink-0" />
                </button>
              ))}
            </CardContent>
          </Card>
        )}
      </section>
    </div>
  );
}

export function ProjectCard({
  project,
  brandId,
}: {
  project: CreativeStrategistProject;
  brandId: string;
}) {
  const [, navigate] = useLocation();
  return (
    <Card
      className="cursor-pointer hover-elevate"
      onClick={() => navigate(`/brands/${brandId}/creative-strategist/projects/${project.id}`)}
      data-testid={`card-project-${project.id}`}
    >
      <CardContent className="p-5 space-y-4">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="text-sm font-semibold truncate">{project.title}</div>
            {project.brandName && (
              <div className="text-xs text-muted-foreground truncate">{project.brandName}</div>
            )}
          </div>
          <Badge
            variant="secondary"
            className={CS_PROJECT_STATUS_CLASSES[project.status] ?? ""}
            data-testid={`badge-project-status-${project.id}`}
          >
            {CS_PROJECT_STATUS_LABELS[project.status] ?? project.status}
          </Badge>
        </div>

        <div className="space-y-1.5">
          <div className="flex items-center justify-between text-xs text-muted-foreground">
            <span>Progress</span>
            <span>{project.progressPercentage}%</span>
          </div>
          <Progress value={project.progressPercentage} className="h-1.5" />
        </div>

        <div className="grid grid-cols-3 gap-3 text-xs">
          <Stat label="Tasks done" value={`${project.stats.tasksComplete}/${project.stats.tasksTotal}`} />
          <Stat label="Awaiting you" value={project.stats.tasksAwaitingUser} />
          <Stat label="Deliverables" value={project.stats.deliverablesTotal} />
        </div>

        {/* Work that ran out of automatic revisions. The card is where a user
            scanning their projects decides which one to open — the flag has to
            be visible from here. (Task #2382.) */}
        {(project.stats.tasksNeedingAttention ?? 0) > 0 && (
          <div
            className="flex items-center gap-1.5 text-xs text-amber-700 dark:text-amber-300"
            data-testid={`card-needs-attention-${project.id}`}
          >
            <AlertTriangle className="h-3.5 w-3.5 shrink-0" />
            {project.stats.tasksNeedingAttention}{" "}
            {project.stats.tasksNeedingAttention === 1 ? "task needs" : "tasks need"} you
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function Stat({ label, value }: { label: string; value: string | number }) {
  return (
    <div>
      <div className="text-sm font-medium">{value}</div>
      <div className="text-muted-foreground">{label}</div>
    </div>
  );
}
